import { useEffect, useRef } from "react";
import { Stack } from "@mui/material";

import type { PlotGeoResponse } from "api/model";
import { useCogOrtho, type LocationOrtho } from "context/CogOrtho";

import AnalysisDateSlider from "./AnalysisDateSlider";
import AnalysisResults from "./AnalysisResults";

interface AnalysisResultsTabProps {
  analyzedOrthos: LocationOrtho[];
  plotsGeo: PlotGeoResponse[];
  siteLabel: string;
  trialId: number;
}

const AnalysisResultsTab: React.FC<AnalysisResultsTabProps> = ({
  analyzedOrthos,
  plotsGeo,
  siteLabel,
  trialId,
}) => {
  const { displayOrtho, orthos } = useCogOrtho();
  const displayedOrthoId = orthos.find((ortho) => ortho.isVisible)?.id ?? null;
  // Land on the latest flight unless the selected one still has results here.
  const resultsOrtho =
    analyzedOrthos.find((ortho) => ortho.id === displayedOrthoId) ??
    analyzedOrthos.at(-1);
  const resultsOrthoId = resultsOrtho?.id ?? null;
  const lastDisplayedOrthoIdRef = useRef<number | null>(null);

  useEffect(() => {
    if (resultsOrthoId == null) {
      lastDisplayedOrthoIdRef.current = null;
      return;
    }
    if (lastDisplayedOrthoIdRef.current !== resultsOrthoId) {
      lastDisplayedOrthoIdRef.current = resultsOrthoId;
      displayOrtho(resultsOrthoId);
    }
  }, [displayOrtho, resultsOrthoId]);

  if (resultsOrthoId == null) {
    return null;
  }

  return (
    <Stack spacing={2}>
      <AnalysisDateSlider
        onChange={displayOrtho}
        orthos={analyzedOrthos}
        selectedOrthoId={resultsOrthoId}
      />
      <AnalysisResults
        orthoId={resultsOrthoId}
        plotsGeo={plotsGeo}
        siteLabel={siteLabel}
        trialId={trialId}
      />
    </Stack>
  );
};

export default AnalysisResultsTab;
